import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isMock = searchParams.get('mock');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (isMock) {
      setStatus('success');
      return;
    }
    if (sessionId) {
      pollStatus(sessionId, 0);
    } else {
      setStatus('success');
    }
  }, []);

  const pollStatus = async (sid, attempt) => {
    if (attempt >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/checkout/status/${sid}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      setAttempts(attempt + 1);
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
      if (attempt >= 3) {
        setStatus('success'); // Assume success if we can't verify
      } else {
        setTimeout(() => pollStatus(sid, attempt + 1), 2000);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-lavender-50 to-cream-50 flex items-center justify-center px-4" data-testid="booking-success-page">
      <Card className="max-w-lg w-full border-0 shadow-2xl">
        <CardContent className="p-12 text-center space-y-6">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-sage-600 mx-auto animate-spin" />
              <h1 className="text-3xl font-serif text-gray-900">Processing Payment...</h1>
              <p className="text-gray-600">Please wait while we confirm your payment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
              <h1 className="text-3xl font-serif text-gray-900" data-testid="booking-success-title">Booking Confirmed!</h1>
              <p className="text-gray-600">Thank you for your booking. You'll receive a confirmation email shortly with your session details.</p>
              <div className="pt-4 space-y-3">
                <Link to="/">
                  <Button className="w-full bg-sage-700 hover:bg-sage-800 text-white rounded-xl">
                    Return Home
                  </Button>
                </Link>
              </div>
            </>
          )}

          {(status === 'failed' || status === 'timeout') && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <h1 className="text-3xl font-serif text-gray-900">Payment Issue</h1>
              <p className="text-gray-600">There was an issue processing your payment. Please try again or contact us for assistance.</p>
              <div className="pt-4 space-y-3">
                <Link to="/booking">
                  <Button className="w-full bg-sage-700 hover:bg-sage-800 text-white rounded-xl">
                    Try Again
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="w-full border-sage-300 text-sage-700 rounded-xl">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingSuccess;
